(() => {
  const ARTICLE_QUERY = `
    query ThepointArticles(
      $first: Int
      $after: String
      $last: Int
      $before: String
      $query: String
    ) {
      articles(
        first: $first
        after: $after
        last: $last
        before: $before
        sortKey: PUBLISHED_AT
        reverse: true
        query: $query
      ) {
        nodes {
          id
          title
          handle
          publishedAt
          excerpt
          onlineStoreUrl
          blog {
            handle
            title
          }
          image {
            url
            altText
            width
            height
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  `;

  const PAGE_SIZE = 12;
  const CATEGORIES = {
    all: { label: 'All', query: null },
    thepoint: { label: 'THEPOINT', query: 'blog_title:"THEPOINT"' },
    articles: { label: 'Articles', query: 'blog_title:"Articles"' },
    press: { label: 'Press', query: 'blog_title:"Press"' },
  };

  const createElement = (tagName, className, text) => {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  const plainText = (html) => {
    const element = document.createElement('div');
    element.innerHTML = html || '';
    return element.textContent.replace(/\s+/g, ' ').trim();
  };

  const truncateWords = (text, limit) => {
    const words = text.split(/\s+/).filter(Boolean);
    return words.length > limit ? `${words.slice(0, limit).join(' ')}…` : words.join(' ');
  };

  const articleUrl = (article) => {
    if (article.onlineStoreUrl) {
      try {
        const url = new URL(article.onlineStoreUrl, window.location.origin);
        if (url.origin === window.location.origin) return `${url.pathname}${url.search}${url.hash}`;
        return url.href;
      } catch (error) {
        // Fall through to the canonical blog path.
      }
    }

    return `/blogs/${article.blog.handle}/${article.handle}`;
  };

  const resizedImageUrl = (source, width) => {
    try {
      const url = new URL(source);
      url.searchParams.set('width', width);
      return url.href;
    } catch (error) {
      return source;
    }
  };

  const createImage = (article, featured = false) => {
    if (!article.image) return null;

    const widths = featured ? [480, 720, 960, 1200, 1500, 1800] : [320, 480, 640, 750, 900];
    const image = document.createElement('img');
    image.src = resizedImageUrl(article.image.url, widths[widths.length - 1]);
    image.srcset = widths.map((width) => `${resizedImageUrl(article.image.url, width)} ${width}w`).join(', ');
    image.sizes = featured
      ? '(min-width: 990px) 58vw, 100vw'
      : '(min-width: 990px) 33vw, (min-width: 750px) 50vw, 100vw';
    image.alt = article.image.altText || article.title;
    image.loading = featured ? 'eager' : 'lazy';
    image.decoding = 'async';
    if (featured) image.setAttribute('fetchpriority', 'high');
    if (article.image.width) image.width = article.image.width;
    if (article.image.height) image.height = article.image.height;
    return image;
  };

  const createDate = (article, formatter) => {
    const paragraph = createElement('p', 'thepoint-meta');
    const time = document.createElement('time');
    time.dateTime = article.publishedAt;
    time.textContent = formatter.format(new Date(article.publishedAt));
    paragraph.append(time);
    return paragraph;
  };

  const createEyebrow = (article) => createElement('p', 'thepoint-eyebrow', article.blog.title);

  const createFeature = (article, formatter) => {
    const feature = createElement('article', 'thepoint-feature page-width');
    const url = articleUrl(article);
    const imageLink = createElement('a', 'thepoint-feature__image');
    imageLink.href = url;
    imageLink.setAttribute('aria-label', article.title);

    const image = createImage(article, true);
    if (image) imageLink.append(image);

    const content = createElement('div', 'thepoint-feature__content');
    const heading = document.createElement('h2');
    const titleLink = document.createElement('a');
    titleLink.href = url;
    titleLink.textContent = article.title;
    heading.append(titleLink);

    const excerpt = createElement(
      'div',
      'thepoint-feature__excerpt',
      truncateWords(plainText(article.excerpt), 42),
    );
    const readMore = createElement('a', 'thepoint-read-more', 'Read the story');
    readMore.href = url;

    content.append(createEyebrow(article), heading);
    if (excerpt.textContent) content.append(excerpt);
    content.append(createDate(article, formatter), readMore);
    feature.append(imageLink, content);
    return feature;
  };

  const createCard = (article, formatter) => {
    const card = createElement('article', 'thepoint-card');
    const url = articleUrl(article);
    const imageLink = createElement('a', 'thepoint-card__image');
    imageLink.href = url;
    imageLink.setAttribute('aria-label', article.title);
    if (article.handle.includes('the-bleusalt-woman')) {
      imageLink.style.setProperty('--thepoint-card-focus', 'right center');
    }

    const image = createImage(article);
    if (image) imageLink.append(image);

    const content = createElement('div', 'thepoint-card__content');
    const heading = document.createElement('h3');
    const titleLink = document.createElement('a');
    titleLink.href = url;
    titleLink.textContent = article.title;
    heading.append(titleLink);

    const excerpt = createElement(
      'p',
      'thepoint-card__excerpt',
      truncateWords(plainText(article.excerpt), 24),
    );

    content.append(createEyebrow(article), heading);
    if (excerpt.textContent) content.append(excerpt);
    content.append(createDate(article, formatter));
    card.append(imageLink, content);
    return card;
  };

  const initializeArchive = (root) => {
    if (root.dataset.thepointInitialized === 'true') return;
    root.dataset.thepointInitialized = 'true';

    const featureRegion = root.querySelector('[data-thepoint-feature-region]');
    const grid = root.querySelector('[data-thepoint-grid]');
    const count = root.querySelector('[data-thepoint-count]');
    const pagination = root.querySelector('[data-thepoint-pagination]');
    const status = root.querySelector('[data-thepoint-status]');
    const filterLinks = [...root.querySelectorAll('[data-thepoint-filter]')];
    const archive = root.querySelector('.thepoint-archive');
    const formatter = new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    const counts = {
      all: Number(root.dataset.countAll) || 0,
      thepoint: Number(root.dataset.countThepoint) || 0,
      articles: Number(root.dataset.countArticles) || 0,
      press: Number(root.dataset.countPress) || 0,
    };
    let controller;

    const stateFromUrl = () => {
      const params = new URLSearchParams(window.location.search);
      const category = Object.prototype.hasOwnProperty.call(CATEGORIES, params.get('category'))
        ? params.get('category')
        : 'all';
      const after = params.get('after');
      const before = params.get('before');
      let page = Math.max(1, Number.parseInt(params.get('page') || '1', 10) || 1);
      if (page > 1 && !after && !before) page = 1;
      return { category, after, before, page };
    };

    const urlForState = (state) => {
      const url = new URL(window.location.href);
      url.searchParams.delete('category');
      url.searchParams.delete('after');
      url.searchParams.delete('before');
      url.searchParams.delete('page');
      if (state.category !== 'all') url.searchParams.set('category', state.category);
      if (state.after) url.searchParams.set('after', state.after);
      if (state.before) url.searchParams.set('before', state.before);
      if (state.page > 1) url.searchParams.set('page', state.page);
      return `${url.pathname}${url.search}${url.hash}`;
    };

    const updateFilters = (category) => {
      filterLinks.forEach((link) => {
        if (link.dataset.thepointFilter === category) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      });
    };

    const createPageLink = (label, state, direction) => {
      const link = createElement('a', 'thepoint-pagination__link', label);
      link.href = urlForState(state);
      link.dataset.thepointPage = direction;
      return link;
    };

    const renderPagination = (state, pageInfo) => {
      pagination.replaceChildren();
      if (!pageInfo.hasPreviousPage && !pageInfo.hasNextPage) return;

      const nav = createElement('nav', 'thepoint-pagination');
      nav.setAttribute('aria-label', 'Article pagination');

      if (pageInfo.hasPreviousPage) {
        nav.append(
          createPageLink(
            'Previous',
            {
              category: state.category,
              before: pageInfo.startCursor,
              after: null,
              page: Math.max(1, state.page - 1),
            },
            'previous',
          ),
        );
      } else {
        nav.append(createElement('span', 'thepoint-pagination__spacer'));
      }

      nav.append(createElement('span', 'thepoint-pagination__current', `Page ${state.page}`));

      if (pageInfo.hasNextPage) {
        nav.append(
          createPageLink(
            'Next',
            {
              category: state.category,
              before: null,
              after: pageInfo.endCursor,
              page: state.page + 1,
            },
            'next',
          ),
        );
      } else {
        nav.append(createElement('span', 'thepoint-pagination__spacer'));
      }

      pagination.append(nav);
    };

    const render = (state, connection) => {
      const articles = connection.nodes;
      const featuredArticle = state.page === 1 ? articles[0] : null;
      const cardArticles = featuredArticle ? articles.slice(1) : articles;

      featureRegion.replaceChildren();
      featureRegion.hidden = !featuredArticle;
      if (featuredArticle) featureRegion.append(createFeature(featuredArticle, formatter));

      grid.replaceChildren();
      if (cardArticles.length) {
        grid.append(...cardArticles.map((article) => createCard(article, formatter)));
      } else if (!featuredArticle) {
        grid.append(createElement('p', 'thepoint-empty', 'No stories found.'));
      }

      const total = counts[state.category];
      count.textContent = `${total} ${total === 1 ? 'article' : 'articles'}`;
      updateFilters(state.category);
      renderPagination(state, connection.pageInfo);

      const start = Math.min((state.page - 1) * PAGE_SIZE + 1, total);
      const end = Math.min(state.page * PAGE_SIZE, total);
      status.textContent = total
        ? `Showing articles ${start} through ${end} of ${total} in ${CATEGORIES[state.category].label}.`
        : `No articles found in ${CATEGORIES[state.category].label}.`;
    };

    const load = async (state, options = {}) => {
      if (controller) controller.abort();
      controller = new AbortController();
      const requestController = controller;
      root.setAttribute('aria-busy', 'true');

      const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      };
      if (root.dataset.storefrontAccessToken) {
        headers['X-Shopify-Storefront-Access-Token'] = root.dataset.storefrontAccessToken;
      }

      try {
        const response = await fetch(root.dataset.storefrontEndpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            query: ARTICLE_QUERY,
            variables: {
              first: state.before ? null : PAGE_SIZE,
              after: state.after || null,
              last: state.before ? PAGE_SIZE : null,
              before: state.before || null,
              query: CATEGORIES[state.category].query,
            },
          }),
          signal: requestController.signal,
        });
        const payload = await response.json();
        if (!response.ok || payload.errors || !payload.data?.articles) {
          throw new Error(payload.errors?.[0]?.message || `Article request failed (${response.status})`);
        }

        render(state, payload.data.articles);
        const nextUrl = urlForState(state);
        if (options.pushHistory) window.history.pushState({}, '', nextUrl);
        else if (nextUrl !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
          window.history.replaceState({}, '', nextUrl);
        }
        if (options.scroll) archive.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Unable to load THEPOINT articles.', error);
          status.textContent = 'The full article archive could not be loaded. Showing available stories.';
        }
      } finally {
        if (controller === requestController) root.removeAttribute('aria-busy');
      }
    };

    filterLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        load(
          { category: link.dataset.thepointFilter, after: null, before: null, page: 1 },
          { pushHistory: true, scroll: true },
        );
      });
    });

    pagination.addEventListener('click', (event) => {
      const link = event.target.closest('[data-thepoint-page]');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      const url = new URL(link.href);
      const state = {
        category: url.searchParams.get('category') || 'all',
        after: url.searchParams.get('after'),
        before: url.searchParams.get('before'),
        page: Number.parseInt(url.searchParams.get('page') || '1', 10),
      };
      load(state, { pushHistory: true, scroll: true });
    });

    window.addEventListener('popstate', () => load(stateFromUrl()));
    load(stateFromUrl());
  };

  document.querySelectorAll('[data-thepoint-unified]').forEach(initializeArchive);
})();
